import React, { useEffect, useRef, useState } from 'react';

interface DirectionsMapProps {
  destination?: google.maps.LatLngLiteral | null;
  className?: string;
  onDirectionsCalculated?: (result: google.maps.DirectionsResult | null) => void;
  onMapInstance?: (mapInstance: google.maps.Map) => void;
  onStreetUpdate?: (street: string | null) => void;
  onStepUpdate?: (stepIndex: number) => void;
}

// Interface for route summary information
interface RouteSummary {
  distance: string;
  duration: string;
  steps: {
    instructions: string;
    distance: string;
    duration: string;
  }[];
}

const DirectionsMap: React.FC<DirectionsMapProps> = ({ 
  destination, 
  className, 
  onDirectionsCalculated,
  onMapInstance,
  onStreetUpdate,
  onStepUpdate
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [userLocation, setUserLocation] = useState<google.maps.LatLngLiteral | null>(null);
  const [userMarker, setUserMarker] = useState<google.maps.Marker | null>(null);
  const [userAccuracyCircle, setUserAccuracyCircle] = useState<google.maps.Circle | null>(null);
  const [destinationMarker, setDestinationMarker] = useState<google.maps.Marker | null>(null);
  const [directionsRenderer, setDirectionsRenderer] = useState<google.maps.DirectionsRenderer | null>(null);
  const [directionsResult, setDirectionsResult] = useState<google.maps.DirectionsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [routeSummary, setRouteSummary] = useState<RouteSummary | null>(null);
  const [locationAccuracy, setLocationAccuracy] = useState<number>(0);
  const [currentSpeed, setCurrentSpeed] = useState<number | null>(null);
  const [currentStreet, setCurrentStreet] = useState<string | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const lastUpdateTimeRef = useRef<number>(0);
  const lastStreetUpdateTimeRef = useRef<number>(0);
  const locationHistoryRef = useRef<google.maps.LatLngLiteral[]>([]);
  const isNavigatingRef = useRef<boolean>(false);
  const currentStepIndexRef = useRef<number>(0);

  // Initialize map with improved settings for street-level accuracy
  useEffect(() => {
    if (!mapRef.current || map) return;

    try {
      // Default location
      const defaultLocation = { lat: 43.6532, lng: -79.3832 }; // Toronto as fallback
      
      // Create map with improved settings for navigation
      const mapInstance = new google.maps.Map(mapRef.current, {
        center: defaultLocation,
        zoom: 19, // Maximum zoom for street level detail
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        mapTypeId: 'roadmap',
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
          }
        ],
        // Improved settings for performance and precision
        clickableIcons: false,
        disableDefaultUI: false,
        gestureHandling: 'cooperative',
        tilt: 0 // Flat view for clearer navigation
      });

      // Create geocoder for reverse geocoding (getting street names)
      geocoderRef.current = new google.maps.Geocoder();

      // Create the DirectionsRenderer with improved settings
      const renderer = new google.maps.DirectionsRenderer({
        map: mapInstance,
        suppressMarkers: true, // We'll use our own markers
        polylineOptions: {
          strokeColor: '#4F46E5', // indigo-600
          strokeWeight: 5,
          strokeOpacity: 0.7,
          zIndex: 1
        },
        preserveViewport: true, // Don't auto-zoom, we'll handle it for better UX
        routeIndex: 0
      });

      setMap(mapInstance);
      setDirectionsRenderer(renderer);
      
      // Pass map instance to parent component if callback provided
      if (onMapInstance) {
        onMapInstance(mapInstance);
      }
      
      // Log successful initialization
      console.log('Map initialized with high precision settings');
    } catch (err) {
      console.error('Error initializing map:', err);
      setError('Failed to initialize Google Maps');
    }
  }, [mapRef, map, onMapInstance]);

  // Get user's location with maximum precision
  useEffect(() => {
    if (!map) return;

    if (navigator.geolocation) {
      // Initialize tracking with highest available accuracy
      const getHighPrecisionLocation = () => {
        // First try - fast position acquisition
        navigator.geolocation.getCurrentPosition(
          handlePositionSuccess,
          (err) => {
            console.error('Initial position error:', err);
            setError('Could not determine your location. Please allow location access and ensure GPS is enabled.');
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
        
        // Then set up continuous tracking with maximum accuracy settings
        if (watchIdRef.current !== null) {
          navigator.geolocation.clearWatch(watchIdRef.current);
        }
        
        watchIdRef.current = navigator.geolocation.watchPosition(
          handlePositionSuccess,
          (err) => {
            console.error('Watch position error:', err);
            setError(`Location tracking error: ${err.message || 'Unknown error'}. Try enabling high-accuracy mode in your device settings.`);
          },
          {
            enableHighAccuracy: true, // Force high accuracy GPS
            timeout: 15000,           // Wait longer for accurate fix
            maximumAge: 0             // Always get fresh positions
          }
        );
      };
      
      // Handle successful position updates with improved accuracy
      const handlePositionSuccess = (position: GeolocationPosition) => {
        // Skip updates that are too frequent (throttle to reduce jitter)
        const now = Date.now();
        const timeSinceLastUpdate = now - lastUpdateTimeRef.current;
        if (timeSinceLastUpdate < 1000 && locationHistoryRef.current.length > 0) {
          return; // Skip this update if less than 1 second since last update
        }
        
        // Extract position data
        const userPos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        
        // Store high-precision data
        const accuracy = position.coords.accuracy;
        const speed = position.coords.speed !== null ? position.coords.speed : null;
        const heading = position.coords.heading;
        
        // Update state with new position data
        setUserLocation(userPos);
        setLocationAccuracy(accuracy);
        setCurrentSpeed(speed);
        
        // Track location history for smoother navigation
        locationHistoryRef.current.push(userPos);
        if (locationHistoryRef.current.length > 5) {
          locationHistoryRef.current.shift(); // Keep last 5 positions
        }
        
        // Calculate a smoothed position using recent history
        let smoothedPosition = userPos;
        if (locationHistoryRef.current.length >= 3) {
          // Apply weighted average to recent positions (most recent has higher weight)
          const positions = locationHistoryRef.current;
          const len = positions.length;
          
          let latSum = 0, lngSum = 0, weightSum = 0;
          for (let i = 0; i < len; i++) {
            const weight = i + 1; // Weight increases with recency
            latSum += positions[i].lat * weight;
            lngSum += positions[i].lng * weight;
            weightSum += weight;
          }
          
          smoothedPosition = {
            lat: latSum / weightSum,
            lng: lngSum / weightSum
          };
        }
        
        // Center map on user's smoothed location for better experience
        if (!destination || !isNavigatingRef.current) {
          map.panTo(smoothedPosition);
        }
        
        // Update user marker and accuracy circle for better visualization
        updateUserLocationMarker(map, smoothedPosition, accuracy, heading);
        
        // Get current street name (throttled to reduce API calls)
        const timeSinceLastStreetUpdate = now - lastStreetUpdateTimeRef.current;
        if (timeSinceLastStreetUpdate > 5000) { // Update street name every 5 seconds
          getStreetName(smoothedPosition);
          lastStreetUpdateTimeRef.current = now;
        }
        
        // Check if we need to update the route during navigation
        if (destination && directionsResult && isNavigatingRef.current) {
          // Get current route information
          const route = directionsResult.routes[0];
          const leg = route.legs[0];
          const steps = leg.steps;
          
          // Find which step we're currently on
          let closestStepIndex = 0;
          let closestDistance = Infinity;
          
          steps.forEach((step, index) => {
            const stepStart = {
              lat: step.start_location.lat(),
              lng: step.start_location.lng()
            };
            
            const distance = getDistanceInMeters(smoothedPosition, stepStart);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestStepIndex = index;
            }
          });
          
          // Update current step if needed
          if (closestStepIndex !== currentStepIndexRef.current) {
            currentStepIndexRef.current = closestStepIndex;
            
            // Notify parent component of step change if callback provided
            if (onStepUpdate) {
              onStepUpdate(closestStepIndex);
            }
          }
          
          // If we've deviated significantly from route, recalculate
          if (closestDistance > 50) { // More than 50m off route
            calculateDirections(smoothedPosition, destination, true);
          }
        }
        
        // Update last update time
        lastUpdateTimeRef.current = now;
      };
      
      // Start high-precision location tracking
      getHighPrecisionLocation();
      
      // Clean up function
      return () => {
        if (watchIdRef.current !== null) {
          navigator.geolocation.clearWatch(watchIdRef.current);
          watchIdRef.current = null;
        }
        
        if (userAccuracyCircle) {
          userAccuracyCircle.setMap(null);
        }
      };
    } else {
      setError('Geolocation is not supported by this browser');
    }
  }, [map, destination, onStepUpdate]);

  // Get street name for current location
  const getStreetName = (position: google.maps.LatLngLiteral) => {
    if (!geocoderRef.current) return;
    
    geocoderRef.current.geocode({ location: position }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        // Extract street information from address components
        const addressComponents = results[0].address_components;
        let street = null;
        
        // Look for the street name in address components
        for (const component of addressComponents) {
          if (component.types.includes('route')) {
            street = component.long_name;
            break;
          }
        }
        
        if (street) {
          setCurrentStreet(street);
          
          // Notify parent component of street update if callback provided
          if (onStreetUpdate) {
            onStreetUpdate(street);
          }
        }
      }
    });
  };

  // Update user location marker with accuracy circle and heading
  const updateUserLocationMarker = (
    map: google.maps.Map, 
    position: google.maps.LatLngLiteral, 
    accuracy: number,
    heading?: number | null
  ) => {
    // Update or create the accuracy circle
    if (userAccuracyCircle) {
      userAccuracyCircle.setCenter(position);
      userAccuracyCircle.setRadius(accuracy);
    } else {
      const circle = new google.maps.Circle({
        center: position,
        radius: accuracy,
        map: map,
        strokeColor: '#4F46E5',
        strokeOpacity: 0.3,
        strokeWeight: 1,
        fillColor: '#4F46E5',
        fillOpacity: 0.1,
        zIndex: 1
      });
      setUserAccuracyCircle(circle);
    }
    
    // Create a more detailed marker icon
    const createUserMarkerIcon = (heading: number | null | undefined) => {
      // If we have a heading, use arrow icon, otherwise circle
      if (heading !== null && heading !== undefined && !isNaN(heading)) {
        return {
          path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 6,
          fillColor: '#4F46E5',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
          rotation: heading
        };
      } else {
        return {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#4F46E5',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2
        };
      }
    };
    
    // Update or create the user marker
    if (userMarker) {
      userMarker.setPosition(position);
      userMarker.setIcon(createUserMarkerIcon(heading));
    } else {
      const marker = new google.maps.Marker({
        position: position,
        map: map,
        icon: createUserMarkerIcon(heading),
        title: 'Your Location',
        zIndex: 1000, // Ensure user marker is on top
        optimized: true // Better performance for frequent updates
      });
      setUserMarker(marker);
    }
  };

  // Calculate the distance between two points in meters
  const getDistanceInMeters = (p1: google.maps.LatLngLiteral, p2: google.maps.LatLngLiteral): number => {
    const R = 6371e3; // Earth radius in meters
    const φ1 = (p1.lat * Math.PI) / 180;
    const φ2 = (p2.lat * Math.PI) / 180;
    const Δφ = ((p2.lat - p1.lat) * Math.PI) / 180;
    const Δλ = ((p2.lng - p1.lng) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance;
  };

  // Clean instructions by removing HTML tags
  const cleanInstructions = (html: string): string => {
    return html.replace(/<\/?[^>]+(>|$)/g, "");
  };

  // Calculate directions with the given origin and destination
  const calculateDirections = (
    origin: google.maps.LatLngLiteral, 
    dest: google.maps.LatLngLiteral,
    isRerouting: boolean = false
  ) => {
    if (!map || !directionsRenderer) return;
    
    const directionsService = new google.maps.DirectionsService();
    
    // Configure route options for higher precision
    const routeOptions = {
      origin: origin,
      destination: dest,
      travelMode: 'DRIVING' as google.maps.TravelMode,
      provideRouteAlternatives: false,
      optimizeWaypoints: true,
      avoidHighways: false,
      avoidTolls: false,
      unitSystem: google.maps.UnitSystem.METRIC,
      drivingOptions: {
        departureTime: new Date() // Use current time for traffic conditions
      }
    };
    
    directionsService.route(
      routeOptions,
      (result, status) => {
        if (status === 'OK' && result) {
          directionsRenderer.setDirections(result);
          setDirectionsResult(result);
          
          if (onDirectionsCalculated) {
            onDirectionsCalculated(result);
          }
          
          // Extract and set route summary
          if (result.routes && result.routes.length > 0 && result.routes[0].legs && result.routes[0].legs.length > 0) {
            const route = result.routes[0];
            const leg = route.legs[0];
            
            const summary: RouteSummary = {
              distance: leg.distance?.text || 'Unknown distance',
              duration: leg.duration?.text || 'Unknown duration',
              steps: leg.steps.map(step => ({
                instructions: cleanInstructions(step.instructions),
                distance: step.distance?.text || '',
                duration: step.duration?.text || ''
              }))
            };
            
            setRouteSummary(summary);
            
            // Reset current step index when route changes
            currentStepIndexRef.current = 0;
            
            // Notify parent of step change if callback provided
            if (onStepUpdate) {
              onStepUpdate(0);
            }
          }
          
          // When rerouting, don't change the map view too much to avoid disorientation
          if (!isRerouting) {
            // Fit map to show the entire route
            const bounds = new google.maps.LatLngBounds();
            bounds.extend(origin);
            bounds.extend(dest);
            
            // Add waypoints to bounds
            result.routes[0]?.overview_path.forEach((point) => {
              bounds.extend(point);
            });
            
            // Set center and zoom for optimal route view
            const distance = result.routes[0]?.legs[0]?.distance?.value || 0;
            
            // For street-level navigation, use a closer zoom
            if (distance < 500) { // Less than 500m
              map.setZoom(18);
              // Center on next turn rather than entire route
              if (result.routes[0]?.legs[0]?.steps && result.routes[0]?.legs[0]?.steps.length > 0) {
                const firstStep = result.routes[0]?.legs[0]?.steps[0];
                map.setCenter({
                  lat: firstStep.start_location.lat(),
                  lng: firstStep.start_location.lng()
                });
              }
            } else if (distance < 2000) { // Less than 2km
              map.setZoom(16);
              map.setCenter(bounds.getCenter());
            } else if (distance < 5000) { // Less than 5km
              map.setZoom(14);
              map.setCenter(bounds.getCenter());
            } else {
              map.setZoom(12);
              map.setCenter(bounds.getCenter());
            }
          }
          
          setError(null);
        } else {
          console.error('Directions request failed:', status);
          setError('Could not calculate directions to the destination');
          setDirectionsResult(null);
          setRouteSummary(null);
          
          if (onDirectionsCalculated) {
            onDirectionsCalculated(null);
          }
        }
      }
    );
  };

  // Set navigation state
  useEffect(() => {
    isNavigatingRef.current = !!destination;
    
    // Reset route info when navigation ends
    if (!destination) {
      currentStepIndexRef.current = 0;
      
      // Notify parent component of step reset
      if (onStepUpdate) {
        onStepUpdate(0);
      }
    }
  }, [destination, onStepUpdate]);

  // Handle destination changes and calculate directions
  useEffect(() => {
    if (!map || !userLocation) return;
    
    // Remove previous destination marker if exists
    if (destinationMarker) {
      destinationMarker.setMap(null);
      setDestinationMarker(null);
    }
    
    // If no destination, clear directions
    if (!destination) {
      if (directionsRenderer) {
        directionsRenderer.setMap(null);
        directionsRenderer.setMap(map);
        setDirectionsResult(null);
        setRouteSummary(null);
        
        if (onDirectionsCalculated) {
          onDirectionsCalculated(null);
        }
      }
      return;
    }
    
    // Create marker for destination
    const marker = new google.maps.Marker({
      position: destination,
      map: map,
      icon: {
        url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
      },
      title: 'Destination',
      zIndex: 999
    });
    setDestinationMarker(marker);
    
    // Calculate directions
    calculateDirections(userLocation, destination, false);
  }, [map, userLocation, destination, directionsRenderer, onDirectionsCalculated]);

  // Update parent component with street name when it changes
  useEffect(() => {
    if (onStreetUpdate && currentStreet !== null) {
      onStreetUpdate(currentStreet);
    }
  }, [currentStreet, onStreetUpdate]);

  return (
    <div className={`relative ${className || 'h-full w-full'}`}>
      {error && (
        <div className="absolute top-0 left-0 right-0 z-10 bg-red-50 p-3 border border-red-200 text-red-600 text-sm">
          {error}
        </div>
      )}
      <div ref={mapRef} className="h-full w-full"></div>
      
      {/* Extended info panel */}
      <div className="absolute bottom-4 left-4 z-10 bg-white/90 backdrop-blur-sm p-2 rounded-lg shadow-md flex flex-col gap-1 max-w-[200px]">
        {/* Current street */}
        {currentStreet && (
          <div className="text-xs font-medium">
            <span className="text-gray-600">Street: </span>
            <span className="text-gray-900">{currentStreet}</span>
          </div>
        )}
        
        {/* Location accuracy */}
        <div className="text-xs">
          <span className="text-gray-600">Accuracy: </span>
          <span className={`${locationAccuracy < 20 ? 'text-green-600' : locationAccuracy < 50 ? 'text-amber-600' : 'text-red-600'}`}>
            {locationAccuracy < 1000 
              ? `${Math.round(locationAccuracy)}m` 
              : `${(locationAccuracy/1000).toFixed(1)}km`}
          </span>
        </div>
        
        {/* Speed information */}
        {currentSpeed !== null && (
          <div className="text-xs">
            <span className="text-gray-600">Speed: </span>
            <span className="text-gray-900">
              {(currentSpeed * 3.6).toFixed(1)} km/h
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectionsMap; 